// La tarjeta del webhook en la 2.1, sin React: cómo se agrupan los eventos, qué dice «Probar» y qué
// dicen los últimos envíos. Los endpoints son los de siempre (`GET/PUT webhook-url/`,
// `PUT webhook-url/test`); lo que agrega el backend (`deliveries`, `data` del test) es opcional: con
// un backend viejo la tarjeta no afirma nada que no sepa.
import {ALERT_RULE_EVENT, indicatorOf} from '../systemRulesModel';
import {systemFamily} from '../rulesModel';

// Los eventos que dispara una alerta. Con las reglas de sistema a mano el grupo sale de ellas
// (`delivery.webhook.event`); esta lista es la red para cuando el catálogo no trae el evento.
const DEFAULT_ALERT_EVENTS = [
    'YESTERDAY_VS_OSD_IS_CRITIC',
    'LAST_7_DAYS_VS_OSD_IS_CRITIC',
    'TODAY_VS_OSD_IS_CRITIC',
    'EVENT_COST_VS_TIME_IS_CRITIC',
    'LAST_PERIOD_VS_ROAS_IS_CRITIC',
    'LAST_PERIOD_VS_ACOS_IS_CRITIC',
    'BUDGET_EXCEEDED',
    'FUNDS_ARE_CRITICAL',
    'FLEX_INDICATOR',
    'GOAL',
    ALERT_RULE_EVENT,
];

export function eventLabelKey(id) {
    return `alerts_center.webhook.event.${id}`;
}

/**
 * `{alerts, changes}`: cada evento con su nombre y, en «Alertas», las reglas que lo disparan
 * (`triggeredBy`, los indicadores; el título lo arma quien dibuja). El orden es el del backend.
 */
export function groupEvents(events, systemRules = []) {
    const byEvent = {};
    systemRules.forEach((rule) => {
        const event = rule.delivery && rule.delivery.webhook && rule.delivery.webhook.event;
        if (!event) return;
        (byEvent[event] = byEvent[event] || []).push(rule);
    });
    const alertIds = new Set([...DEFAULT_ALERT_EVENTS, ...Object.keys(byEvent)]);
    const alerts = [];
    const changes = [];
    (events || []).forEach((event) => {
        if (alertIds.has(event.id)) alerts.push({...event, triggeredBy: byEvent[event.id] || []});
        else changes.push(event);
    });
    return {alerts, changes};
}

export function triggeredByIndicators(group) {
    return (group.triggeredBy || []).map(indicatorOf).filter(Boolean);
}

function isSuccessCode(code) {
    return typeof code === 'number' && code >= 200 && code < 300;
}

/**
 * Qué dice «Probar». Honesto: hasta que el backend sumó `data`, el test respondía OK aunque la URL
 * contestara 404 (sólo atrapaba excepciones). Con `data` se dice lo que pasó; sin él, sólo que se
 * envió.
 *
 * `{tone: 'ok'|'error'|'neutral', key, code}`.
 */
export function testOutcome(response) {
    if (!response || response.status !== 'OK') {
        return {tone: 'error', key: 'alerts_center.webhook.test_error', code: null};
    }
    const data = response.data && typeof response.data === 'object' ? response.data : null;
    if (!data || typeof data.success !== 'boolean') {
        return {tone: 'neutral', key: 'alerts_center.webhook.test_sent', code: null};
    }
    const code = typeof data.response_status_code === 'number' ? data.response_status_code : null;
    if (data.success) return {tone: 'ok', key: 'alerts_center.webhook.test_ok', code};
    return {tone: 'error', key: failureKey(code), code};
}

function failureKey(code) {
    if (code === 404 || code === 410) return 'alerts_center.webhook.fail_gone';
    if (typeof code === 'number' && code >= 400 && code < 500) return 'alerts_center.webhook.fail_rejected';
    return 'alerts_center.webhook.fail_server';
}

/**
 * El estado de los últimos envíos (`deliveries` del GET), o `null` si el backend no lo manda.
 *
 * Falla cuando el último envío no fue 2xx: desde el último que llegó (`since`), o desde siempre.
 */
export function deliveriesState(deliveries) {
    if (!deliveries || typeof deliveries !== 'object') return null;
    const sent = Number(deliveries.sent_7d || 0);
    const failed = Number(deliveries.failed_7d || 0);
    if (!deliveries.last_sent_at) return {state: 'idle', sent, failed};
    const code = typeof deliveries.last_status_code === 'number' ? deliveries.last_status_code : null;
    if (isSuccessCode(code)) {
        return {state: 'ok', sent, failed, lastSuccessAt: deliveries.last_success_at || deliveries.last_sent_at};
    }
    return {
        state: 'failing',
        sent,
        failed,
        code,
        reasonKey: failureKey(code),
        since: deliveries.last_success_at || null,
    };
}

export function sameSelection(a, b) {
    return [...(a || [])].sort().join(',') === [...(b || [])].sort().join(',');
}

// ------------------------------------------------------------------ v2: tarjeta y panel ------------

// Adónde va el webhook (contrato v2 §3, `webhook.destination`): sólo elige los pasos del panel.
export const DESTINATIONS = ['slack', 'gchat', 'make', 'zapier', 'other'];

export const WEBHOOK_STATE_LOADING = 'loading';
export const WEBHOOK_STATE_UNKNOWN = 'unknown';
export const WEBHOOK_STATE_UNAVAILABLE = 'unavailable';
export const WEBHOOK_STATE_NONE = 'none';
export const WEBHOOK_STATE_OK = 'ok';
export const WEBHOOK_STATE_FAILING = 'failing';

/**
 * El estado de la tarjeta del webhook. Manda el `webhook` del listado (contrato v2 §3: configurado, pausado,
 * adónde va, los últimos 7 días); sin él (un backend sin el frente del envío) se cae a lo que dijo
 * `GET webhook-url/` (`legacy`, el estado de la tarjeta de siempre), que sólo sabe si hay URL.
 *
 * `{state, destination, last7d, reason, since, code}`.
 */
export function webhookStatus(fromPayload, legacy = null) {
    if (fromPayload && typeof fromPayload === 'object') {
        if (fromPayload.available === false) return {state: WEBHOOK_STATE_UNAVAILABLE};
        if (!fromPayload.configured) return {state: WEBHOOK_STATE_NONE, destination: fromPayload.destination || null};
        const failure = fromPayload.last_failure && typeof fromPayload.last_failure === 'object' ? fromPayload.last_failure : {};
        const code = typeof failure.status_code === 'number' ? failure.status_code
            : (typeof failure.code === 'number' ? failure.code : null);
        if (fromPayload.paused) {
            return {
                state: WEBHOOK_STATE_FAILING,
                destination: fromPayload.destination || null,
                reason: fromPayload.paused_reason || null,
                since: fromPayload.paused_on || failure.at || failure.on || null,
                code,
            };
        }
        const last7d = fromPayload.last_7d && typeof fromPayload.last_7d === 'object' ? fromPayload.last_7d : null;
        return {state: WEBHOOK_STATE_OK, destination: fromPayload.destination || null, last7d};
    }
    if (!legacy || legacy.status === 'loading') return {state: WEBHOOK_STATE_LOADING};
    if (legacy.status === 'no_module') return {state: WEBHOOK_STATE_UNAVAILABLE};
    if (legacy.status !== 'ok') return {state: WEBHOOK_STATE_UNKNOWN};
    return legacy.url ? {state: WEBHOOK_STATE_OK, destination: null, last7d: null} : {state: WEBHOOK_STATE_NONE, destination: null};
}

/** ¿Prender el webhook de una fila puede mandar algo ya, o hay que conectarlo primero? */
export function webhookNeedsSetup(status) {
    return status.state === WEBHOOK_STATE_NONE;
}

/**
 * El `webhook` del listado después de guardar una URL en el panel, mientras llega el de verdad: conectado y con
 * su destino. La pausa NO se toca: el backend despausa sólo con una URL distinta o una prueba con éxito, y
 * guardar la misma URL sin probar la deja pausada (review C-av2-fe-centro-04). Lo corrige la recarga.
 */
export function connectedWebhook(current, destination) {
    return {...(current || {}), configured: true, available: true, destination};
}

/** El `webhook` (contrato v2 §3) que devuelve un PUT, si vino: `{data: {webhook}}` o `{result: {webhook}}`. */
export function webhookFromResponse(response) {
    const body = response && (response.data || response.result);
    const webhook = body && body.webhook;
    return webhook && typeof webhook === 'object' ? webhook : null;
}

const DESTINATION_HOSTS = [
    ['slack', /(^|\.)slack\.com$/],
    ['gchat', /(^|\.)chat\.googleapis\.com$/],
    ['make', /(^|\.)make\.com$/],
    ['zapier', /(^|\.)zapier\.com$/],
];

/**
 * El destino que se deduce de la dirección (`hooks.slack.com` → Slack…), o `null` si no se reconoce. Sirve para
 * no inventar «Slack» cuando la agencia no lo dijo (review C-av2-fe-centro-17).
 */
export function inferDestination(url) {
    let host = '';
    try {
        host = new URL(String(url || '').trim()).hostname.toLowerCase();
    } catch (e) {
        return null;
    }
    const found = DESTINATION_HOSTS.find(([, pattern]) => pattern.test(host));
    return found ? found[0] : null;
}

/**
 * Los eventos que viajan al guardar el webhook en una agencia convertida: los «Cambios en tus cuentas» que eligió
 * la agencia (de `selected`) y, de los que gobiernan las filas, sólo los de las filas que tienen el webhook prendido
 * AHORA. Reenviar la foto que se leyó al cargar apagaba el evento que una fila prendió después (README v2 §4.3: el
 * evento es derivado de las filas; review C-av2-fe-centro-03).
 *
 * `governed`: los eventos que alguna fila gobierna. `on`: los de las filas con webhook prendido.
 */
export function eventsForSave(selected, {governed = [], on = []} = {}) {
    const ruled = new Set(governed);
    return [...new Set([...(selected || []).filter((id) => !ruled.has(id)), ...on])];
}

/** Lo mínimo que tiene que ser una dirección para que valga la pena probarla. */
export function looksLikeWebhookUrl(url) {
    return /^https:\/\/\S+\.\S+/.test(String(url || '').trim());
}

// Los eventos del webhook que dispara cada aviso de control, por si la fila no trae `webhook_event`: en
// «Avanzado» no se ofrecen (los decide la fila).
const CONTROL_EVENTS_BY_KEY = {
    URL_DOWN: ['DOWN_URL'],
    NO_IMPRESSIONS: ['BUSINESS_UNIT_HAS_NO_IMPRESSIONS', 'BUSINESS_UNIT_HAS_IMPRESSIONS'],
    DAILY_LIMIT: ['DAILY_LIMIT_EXCEEDED'],
    FUNDS: ['FUNDS_ARE_CRITICAL'],
};

/** Los eventos del webhook que ya tienen una fila que los prende y apaga. */
export function rowWebhookEvents(rows) {
    const events = new Set();
    rows.forEach((rule) => {
        const event = rule.delivery && rule.delivery.webhook_event;
        if (event) events.add(event);
        (CONTROL_EVENTS_BY_KEY[systemFamily(rule.system_key)] || []).forEach((item) => events.add(item));
    });
    return [...events].sort();
}
